import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { getRenderedFieldByType } from "../../entries/utils/getRenderedFieldByType";
import { Flex } from "../../../components/Flex";
import { AtiraToast } from "../../../utils/AtiraToast";
import { onSetTypedCRUDErrors } from "../../../utils/onSetTypedCRUDErrors";
import { moduleActions } from "../../../redux/module/module.slice";
import isEmpty from "lodash/isEmpty";
import { Lengths } from "../../../model/shared/enum/Lengths.enum";
import { Drawer } from "../../../components/Drawer";
import { UpdateModuleInstanceDto } from "../../../model/module/dto/module-instance/UpdateModuleInstanceDto";
import { moduleSliceSelectors } from "../../../redux/module/module.selector";
import { PageMeta } from "../../../model/shared/meta/PageMeta";
import { DrawerDefaultsExtra } from "../../tasks/components/DrawerDefaultsExtra";
import { Input } from "../../../components/Input";
import { BaseModuleInstance } from "../../../model/module/types/module-instance/BaseModuleInstance";
import { InputTypes } from "../../../model/form/types/InputTypes.enum";

type FixedModuleInstanceUpdateDrawerProps = {
  open: boolean;
  onClose: VoidFunction;
  instance?: BaseModuleInstance | null;
};

export const FixedModuleInstanceUpdateDrawer: React.FC<
  FixedModuleInstanceUpdateDrawerProps
> = ({ open = false, onClose, instance }) => {
  const [loading, setLoading] = useState(false);

  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { control, setError, reset, getValues, handleSubmit } =
    useForm<UpdateModuleInstanceDto>();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const instancesPage = useAppSelector(
    moduleSliceSelectors.selectInstancesPage,
  );
  const instancesPageSize = useAppSelector(
    moduleSliceSelectors.selectInstancesPageSize,
  );
  const parentModuleDefinition =
    useAppSelector((s) =>
      moduleSliceSelectors.selectModuleDefinitionById(
        s,
        instance?.parentId || "",
      ),
    ) || instance?.parent;

  const watchedValues = useWatch({
    control,
    name: ["fields", "name"],
    defaultValue: {
      fields: instance?.fields || {},
      name: instance?.name || "",
    },
  });
  const [fields, name] = watchedValues;

  const requiredInputs = useMemo(() => {
    return parentModuleDefinition?.inputs?.filter(
      (input) => input?.settings?.required === true,
    );
  }, [parentModuleDefinition?.inputs]);

  const isDisabled = useMemo(() => {
    if (!name) {
      return true;
    }

    return requiredInputs?.some((input) => {
      if (input.type.toLowerCase() === InputTypes.DATE.toLowerCase()) {
        return !fields?.[input.name];
      }

      return isEmpty(fields?.[input.name]);
    });
  }, [fields, name, requiredInputs]);

  const renderedInputs = useMemo(() => {
    return parentModuleDefinition?.inputs?.map((input) => (
      <Controller
        key={input._id}
        name={`fields.${input.name}`}
        control={control}
        rules={{
          required: !!input?.settings?.required,
          maxLength: {
            value: input?.settings?.maxChar || Lengths.DESCRIPTION,
            message: t("error.length.less", {
              length: input?.settings?.maxChar || Lengths.DESCRIPTION,
            }),
          },
        }}
        render={({ field, fieldState }) =>
          getRenderedFieldByType(
            input,
            field,
            fieldState,
            input.label,
            undefined,
            {
              possibleBookedSlots: parentModuleDefinition?._possibleBookedSlots,
            },
          )
        }
      />
    ));
  }, [
    parentModuleDefinition?.inputs,
    parentModuleDefinition?._possibleBookedSlots,
    control,
    t,
  ]);

  const localOnClose = () => {
    onClose();
    reset({
      name: instance?.name || "",
      fields: instance?.fields || {},
      moduleDefinitionId: instance?.parentId || "",
      moduleInstanceId: instance?._id || "",
      userId,
    });
  };

  const onSave = async () => {
    try {
      setLoading(true);

      if (isEmpty(instance)) {
        return;
      }

      const dto = getValues();

      await dispatch(moduleActions.updateModuleInstance(dto)).unwrap();

      await dispatch(
        moduleActions.getModuleInstances({
          userId,
          moduleDefinitionId: instance.parentId,
          keyword: "",
          meta: PageMeta.create({
            page: instancesPage - 1,
            count: instancesPageSize,
          }),
        }),
      ).unwrap();

      localOnClose();

      AtiraToast.success(t("modules.instance.update.success"));
    } catch (e) {
      console.log(e);
      AtiraToast.apiError(e);
      onSetTypedCRUDErrors(e, setError);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && instance) {
      reset({
        userId,
        fields: instance?.fields || {},
        moduleInstanceId: instance._id,
        moduleDefinitionId: instance.parentId,
        name: instance?.name,
      });
    }
  }, [open, instance, userId, reset]);

  if (isEmpty(instance) || !parentModuleDefinition) {
    return null;
  }

  return (
    <Drawer
      open={open}
      onClose={localOnClose}
      title={t("modules.instance.update.title", {
        moduleDefinition: parentModuleDefinition.name,
      })}
      maskClosable={false}
      extra={
        <DrawerDefaultsExtra
          onSubmit={handleSubmit(onSave)}
          onCancel={localOnClose}
          config={{
            loading,
            editMode: true,
            disabled: isDisabled,
          }}
        />
      }
      size="large"
      key={`${parentModuleDefinition._id}-${instance._id}`}
    >
      <Flex width={"100%"} flexDirection="column" gap="m">
        <Controller
          control={control}
          name="name"
          rules={{
            required: {
              value: true,
              message: t("common.fieldRequired"),
            },
            maxLength: {
              value: Lengths.DESCRIPTION,
              message: t("error.length.less", {
                name: t("modules.instance.name"),
                length: Lengths.DESCRIPTION,
              }),
            },
          }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Input
              title={t("modules.instance.name.title")}
              value={value}
              onChange={onChange}
              valid={!error}
              errorMessage={error?.message}
              maxLength={Lengths.DESCRIPTION}
              info={t("modules.instance.name.info")}
              required
            />
          )}
        />

        {renderedInputs}
      </Flex>
    </Drawer>
  );
};
